// src/components/staffing/AIDecisionAdvisor.jsx
import React, { useState, useEffect } from 'react';
import PropTypes from 'prop-types';
import {
  Box,
  Typography,
  Grid,
  Card,
  CardContent,
  Chip,
  Alert,
  AlertTitle,
  Button,
  CircularProgress,
  Paper,
  Tooltip
} from '@mui/material';
import {
  EmojiEvents as TrophyIcon,
  Description as SummaryIcon
} from '@mui/icons-material';
import { apiFetch } from '../../utils/api';
import { BRAND_PURPLE, COLOR_GOOD, COLOR_WARN, COLOR_POOR, BG_SURFACE, BG_ELEVATED } from '../../utils/colors';
import { Logger } from '../../utils/logger';
import ExecutiveSummaryModal from './ExecutiveSummaryModal';

const logger = new Logger('AIDecisionAdvisor');

/**
 * AI-Powered Decision Advisor for Team Lineup Comparison
 *
 * Provides intelligent, Claude-powered analysis with:
 * - Clear winner recommendation
 * - Decision framework ("Choose X if...")
 * - Trade-off analysis
 * - Red flag detection
 * - Executive summary generation
 */
const AIDecisionAdvisor = ({ comparisonData, selectedConfigIds, onClose, opportunityMeta = {} }) => {
  const [loading, setLoading] = useState(false);
  const [aiAnalysis, setAiAnalysis] = useState(null);
  const [error, setError] = useState(null);

  // User priorities state
  const [priorities, setPriorities] = useState({
    speed: false,
    cost: false,
    quality: false,
    risk: false
  });

  // Executive summary modal
  const [summaryModalOpen, setSummaryModalOpen] = useState(false);

  // Track if current analysis is from cache
  const [isCached, setIsCached] = useState(false);

  // Cache for AI analysis results (stored in session storage)
  const getCacheKey = () => {
    const sortedIds = [...selectedConfigIds].sort();
    const priorityKey = Object.keys(priorities).filter(k => priorities[k]).sort().join(',');
    return `ai-analysis-${sortedIds.join('-')}-${priorityKey}`;
  };

  const getCachedAnalysis = () => {
    try {
      const cacheKey = getCacheKey();
      const cached = sessionStorage.getItem(cacheKey);
      if (cached) {
        const { data, timestamp } = JSON.parse(cached);
        // Cache valid for 1 hour
        const oneHour = 60 * 60 * 1000;
        if (Date.now() - timestamp < oneHour) {
          logger.debug('✅ Using cached AI analysis');
          return data;
        }
      }
    } catch (err) {
      logger.error('Error reading cache:', err);
    }
    return null;
  };

  const setCachedAnalysis = (data) => {
    try {
      const cacheKey = getCacheKey();
      sessionStorage.setItem(cacheKey, JSON.stringify({
        data,
        timestamp: Date.now()
      }));
      logger.debug('💾 Cached AI analysis');
    } catch (err) {
      logger.error('Error writing cache:', err);
    }
  };

  // Auto-load AI analysis on mount
  useEffect(() => {
    if (selectedConfigIds && selectedConfigIds.length >= 2) {
      // Check cache first
      const cached = getCachedAnalysis();
      if (cached) {
        setAiAnalysis(cached);
        setIsCached(true);
      } else {
        loadAIAnalysis();
      }
    }
  }, []); // Only on mount

  const loadAIAnalysis = async () => {
    try {
      setLoading(true);
      setError(null);

      const prioritiesPayload = Object.keys(priorities).some(k => priorities[k]) ? priorities : null;

      const response = await apiFetch(
        'team-optimization/compare/ai-analysis',
        {
          method: 'POST',
          body: JSON.stringify({
            configuration_ids: selectedConfigIds,
            user_priorities: prioritiesPayload,
            project_context: {} // Could be enhanced with project data
          })
        }
      );

      if (!response.ok) {
        const errorData = await response.json().catch(() => ({}));
        throw new Error(errorData.error || errorData.message || `HTTP error! status: ${response.status}`);
      }

      const data = await response.json();

      if (data.status === 'success') {
        setAiAnalysis(data.ai_analysis);
        setIsCached(false); // Fresh from API
        // Cache the successful result
        setCachedAnalysis(data.ai_analysis);
      } else {
        throw new Error(data.message || 'AI analysis failed');
      }
    } catch (err) {
      logger.error('Error loading AI analysis:', err);
      setError(err.message || 'Failed to load AI analysis');
    } finally {
      setLoading(false);
    }
  };

  const handlePriorityChange = (priority) => {
    setPriorities(prev => ({
      ...prev,
      [priority]: !prev[priority]
    }));
  };

  const handleReanalyze = () => {
    // Check cache first before making API call
    const cached = getCachedAnalysis();
    if (cached) {
      logger.debug('✅ Using cached analysis for new priorities');
      setAiAnalysis(cached);
      setIsCached(true);
    } else {
      loadAIAnalysis();
    }
  };

  const getSeverityColor = (severity) => {
    switch (severity?.toLowerCase()) {
      case 'critical': return 'error';
      case 'warning': return 'warning';
      case 'note': return 'info';
      default: return 'default';
    }
  };

  const getConfidenceLevelColor = (level) => {
    switch (level?.toLowerCase()) {
      case 'high': return 'success';
      case 'medium': return 'warning';
      case 'low': return 'error';
      default: return 'default';
    }
  };

  if (loading) {
    return (
      <Box sx={{ display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center', py: 8, bgcolor: BG_SURFACE }}>
        <CircularProgress size={60} />
        <Typography variant="h6" sx={{ mt: 3, color: '#fff' }}>
          Concierge is analyzing your team lineups...
        </Typography>
        <Typography variant="body2" sx={{ mt: 1, color: '#b0b0b0' }}>
          Evaluating metrics, team composition, and strategic fit
        </Typography>
      </Box>
    );
  }

  if (error) {
    return (
      <Box sx={{ p: 3, bgcolor: BG_SURFACE }}>
        <Alert severity="error" sx={{ mt: 2 }}>
          <AlertTitle>Analysis Error</AlertTitle>
          {error}
          <Button onClick={handleReanalyze} variant="outlined" size="small" sx={{ mt: 2 }}>
            Try Again
          </Button>
        </Alert>
      </Box>
    );
  }

  if (!aiAnalysis) {
    return (
      <Box sx={{ textAlign: 'center', py: 4, bgcolor: BG_SURFACE }}>
        <Typography variant="body1" sx={{ color: '#b0b0b0' }}>
          No Concierge analysis available. Click "Analyze" to get started.
        </Typography>
        <Button variant="contained" onClick={handleReanalyze} sx={{ mt: 2 }}>
          Analyze Lineups
        </Button>
      </Box>
    );
  }

  const getScoreColor = (score) => {
    if (score == null) return '#9e9e9e';
    if (score >= 7) return COLOR_GOOD;
    if (score >= 5) return COLOR_WARN;
    return '#cf6679';
  };

  const getMarginColor = (pct) => {
    if (pct == null) return '#9e9e9e';
    if (pct >= 50) return COLOR_GOOD;
    if (pct >= 30) return COLOR_WARN;
    return '#cf6679';
  };

  const scoreFields = [
    { key: 'overall',   label: 'Overall' },
    { key: 'technical', label: 'Tech' },
    { key: 'chemistry', label: 'Chemistry' },
    { key: 'availability', label: 'Availability' },
    { key: 'experience', label: 'Experience' },
  ];

  const CARD_BORDER = `${BRAND_PURPLE}26`;
  const CARD_SX = { mb: 2, border: `1px solid ${CARD_BORDER}`, borderRadius: 2, bgcolor: BG_ELEVATED };

  return (
    <Box sx={{ p: 3, bgcolor: BG_SURFACE, color: '#fff' }}>

      {/* Side-by-side score summary */}
      {comparisonData?.configurations?.length > 0 && (
        <Card sx={{ ...CARD_SX, mb: 3 }}>
          <CardContent sx={{ pb: '12px !important' }}>
            <Typography variant="subtitle2" sx={{ fontWeight: 'bold', color: BRAND_PURPLE, mb: 1.5 }}>
              Score Summary
            </Typography>
            <Grid container spacing={2}>
              {comparisonData.configurations.map((config, i) => {
                const scores = config.team_analysis_data?.scores || {};
                const marginRaw = config.team_analysis_data?.financialView?.blendedMarginPct;
                const marginPct = marginRaw ? parseFloat(marginRaw) : (config.margin_percentage || config.comparison_metrics?.margin_percentage || null);
                const isRecommended = aiAnalysis?.recommended_lineup === config.configuration_name;
                return (
                  <Grid item xs={12} sm key={i}>
                    <Box sx={{ p: 1.5, borderRadius: 1, bgcolor: isRecommended ? `${COLOR_GOOD}10` : 'rgba(255,255,255,0.03)', border: isRecommended ? `1px solid ${COLOR_GOOD}40` : `1px solid ${CARD_BORDER}` }}>
                      <Typography variant="caption" sx={{ fontWeight: 'bold', color: isRecommended ? COLOR_GOOD : '#fff', display: 'block', mb: 1, fontSize: '0.75rem' }}>
                        {isRecommended && '✓ '}{config.configuration_name}
                      </Typography>
                      <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 0.5 }}>
                        {scoreFields.map(({ key, label }) => {
                          const val = scores[key];
                          return (
                            <Box key={key} sx={{ display: 'flex', flexDirection: 'column', alignItems: 'center', minWidth: 52, bgcolor: 'rgba(0,0,0,0.3)', borderRadius: 1, px: 0.75, py: 0.5 }}>
                              <Typography variant="caption" sx={{ color: '#9e9e9e', fontSize: '0.6rem', lineHeight: 1 }}>{label}</Typography>
                              <Typography variant="caption" sx={{ fontWeight: 'bold', color: getScoreColor(val), fontSize: '0.75rem', lineHeight: 1.4 }}>
                                {val != null ? val.toFixed(1) : '—'}
                              </Typography>
                            </Box>
                          );
                        })}
                        <Box sx={{ display: 'flex', flexDirection: 'column', alignItems: 'center', minWidth: 52, bgcolor: 'rgba(0,0,0,0.3)', borderRadius: 1, px: 0.75, py: 0.5 }}>
                          <Typography variant="caption" sx={{ color: '#9e9e9e', fontSize: '0.6rem', lineHeight: 1 }}>Margin</Typography>
                          <Typography variant="caption" sx={{ fontWeight: 'bold', color: getMarginColor(marginPct), fontSize: '0.75rem', lineHeight: 1.4 }}>
                            {marginPct != null ? `${marginPct.toFixed(0)}%` : '—'}
                          </Typography>
                        </Box>
                      </Box>
                    </Box>
                  </Grid>
                );
              })}
            </Grid>
          </CardContent>
        </Card>
      )}

      {/* Winner Recommendation Card */}
      <Card sx={{ mb: 3, background: `linear-gradient(135deg, ${BRAND_PURPLE}1A 0%, ${BRAND_PURPLE}07 100%)`, border: `1px solid ${BRAND_PURPLE}4D`, borderRadius: 2 }}>
        <CardContent sx={{ p: 2.5 }}>
          <Box sx={{ display: 'flex', alignItems: 'flex-start', gap: 2 }}>
            <TrophyIcon sx={{ fontSize: 32, color: BRAND_PURPLE, mt: 0.5, flexShrink: 0 }} />
            <Box sx={{ flex: 1 }}>
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, flexWrap: 'wrap', mb: 0.5 }}>
                <Typography variant="subtitle2" sx={{ fontWeight: 'bold', color: BRAND_PURPLE }}>
                  Recommended Lineup
                </Typography>
                <Chip
                  label={`${aiAnalysis.confidence_level?.toUpperCase()} Confidence`}
                  size="small"
                  sx={{ height: 20, fontSize: '0.65rem', bgcolor: `${BRAND_PURPLE}22`, color: BRAND_PURPLE }}
                />
                {isCached && (
                  <Chip label="Cached" size="small" sx={{ height: 20, fontSize: '0.65rem', bgcolor: 'rgba(255,255,255,0.06)', color: '#9e9e9e' }} />
                )}
              </Box>
              <Typography variant="h6" sx={{ fontWeight: 'bold', color: COLOR_GOOD, mb: 1 }}>
                {aiAnalysis.recommended_lineup}
              </Typography>
              <Typography variant="body2" sx={{ lineHeight: 1.6, color: '#e0e0e0' }}>
                {aiAnalysis.winner_summary}
              </Typography>
              {aiAnalysis.confidence_explanation && (
                <Typography variant="caption" sx={{ mt: 1, display: 'block', fontStyle: 'italic', color: '#9e9e9e', lineHeight: 1.5 }}>
                  {aiAnalysis.confidence_explanation}
                </Typography>
              )}
            </Box>
          </Box>
        </CardContent>
      </Card>

      {/* Decision Framework */}
      {aiAnalysis.decision_framework && (
        <Card elevation={1} sx={CARD_SX}>
          <CardContent sx={{ p: 2 }}>
            <Typography variant="subtitle2" sx={{ fontWeight: 'bold', color: BRAND_PURPLE, mb: 1.5 }}>
              Decision Framework
            </Typography>
            <Grid container spacing={2}>
              {Object.entries(aiAnalysis.decision_framework).map(([key, scenarios]) => {
                const lineupLetter = key.replace('choose_lineup_', '').replace('_if', '');
                const configIndex = lineupLetter.toLowerCase() === 'a' ? 0 : 1;
                const configName = comparisonData?.configurations?.[configIndex]?.configuration_name || `Lineup ${lineupLetter.toUpperCase()}`;
                return (
                  <Grid item xs={12} md={6} key={key}>
                    <Box sx={{ bgcolor: 'rgba(0,0,0,0.15)', borderRadius: 1.5, p: 1.5, height: '100%' }}>
                      <Typography variant="caption" sx={{ fontWeight: 'bold', color: BRAND_PURPLE, display: 'block', mb: 1 }}>
                        Choose "{configName}" if:
                      </Typography>
                      {scenarios.map((scenario, idx) => (
                        <Box key={idx} sx={{ display: 'flex', alignItems: 'flex-start', mb: 0.75 }}>
                          <Box sx={{ width: 4, height: 4, borderRadius: '50%', bgcolor: BRAND_PURPLE, mt: 1.2, mr: 1.25, flexShrink: 0 }} />
                          <Typography variant="body2" sx={{ lineHeight: 1.5, color: '#e0e0e0', fontSize: '0.83rem' }}>{scenario}</Typography>
                        </Box>
                      ))}
                    </Box>
                  </Grid>
                );
              })}
            </Grid>
          </CardContent>
        </Card>
      )}

      {/* Trade-off Analysis */}
      {aiAnalysis.trade_off_analysis?.length > 0 && (
        <Card elevation={1} sx={CARD_SX}>
          <CardContent sx={{ p: 2 }}>
            <Typography variant="subtitle2" sx={{ fontWeight: 'bold', color: BRAND_PURPLE, mb: 1.5 }}>
              Trade-off Analysis
            </Typography>
            {aiAnalysis.trade_off_analysis.map((tradeoff, idx) => {
              const impactColor = tradeoff.impact_level === 'high' ? COLOR_WARN : tradeoff.impact_level === 'low' ? COLOR_GOOD : '#9e9e9e';
              return (
                <Box key={idx} sx={{ display: 'flex', alignItems: 'flex-start', mb: idx < aiAnalysis.trade_off_analysis.length - 1 ? 1.5 : 0, gap: 1 }}>
                  <Typography variant="caption" sx={{ fontWeight: 'bold', flexShrink: 0, color: '#9e9e9e', mt: 0.1 }}>{idx + 1}.</Typography>
                  <Box sx={{ flex: 1, minWidth: 0 }}>
                    <Typography variant="caption" sx={{ fontWeight: 'bold', color: '#fff', display: 'block', mb: 0.25 }}>{tradeoff.comparison}</Typography>
                    <Typography variant="caption" sx={{ color: '#b0b0b0', lineHeight: 1.5, display: 'block' }}>{tradeoff.trade_off}</Typography>
                  </Box>
                  {tradeoff.impact_level && (
                    <Chip label={tradeoff.impact_level} size="small" sx={{ height: 18, fontSize: '0.6rem', flexShrink: 0, bgcolor: `${impactColor}22`, color: impactColor }} />
                  )}
                </Box>
              );
            })}
          </CardContent>
        </Card>
      )}

      {/* Flags & Considerations */}
      {aiAnalysis.red_flags?.length > 0 && (
        <Card elevation={1} sx={CARD_SX}>
          <CardContent sx={{ p: 2 }}>
            <Typography variant="subtitle2" sx={{ fontWeight: 'bold', color: BRAND_PURPLE, mb: 1.5 }}>
              Flags &amp; Considerations
            </Typography>
            <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 0.75 }}>
              {aiAnalysis.red_flags.map((flag, idx) => {
                const severityColor = flag.severity === 'critical' ? COLOR_POOR : flag.severity === 'warning' ? COLOR_WARN : BRAND_PURPLE;
                const label = flag.lineup ? `${flag.lineup}: ${flag.issue}` : flag.issue;
                return (
                  <Tooltip key={idx} title={flag.mitigation || ''} placement="top" arrow>
                    <Chip
                      label={label}
                      size="small"
                      sx={{ bgcolor: `${severityColor}18`, color: severityColor, fontSize: '0.7rem', cursor: flag.mitigation ? 'help' : 'default' }}
                    />
                  </Tooltip>
                );
              })}
            </Box>
          </CardContent>
        </Card>
      )}

      {/* Key Differentiators */}
      {aiAnalysis.key_differentiators?.length > 0 && (
        <Card elevation={1} sx={CARD_SX}>
          <CardContent sx={{ p: 2 }}>
            <Typography variant="subtitle2" sx={{ fontWeight: 'bold', color: BRAND_PURPLE, mb: 1.5 }}>
              Key Differentiators
            </Typography>
            {aiAnalysis.key_differentiators.map((diff, idx) => (
              <Box key={idx} sx={{ display: 'flex', alignItems: 'flex-start', mb: idx < aiAnalysis.key_differentiators.length - 1 ? 1.25 : 0, gap: 1.25 }}>
                <Box sx={{ width: 8, height: 8, borderRadius: '50%', bgcolor: BRAND_PURPLE, mt: 0.6, flexShrink: 0 }} />
                <Box sx={{ flex: 1, minWidth: 0 }}>
                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 0.2, flexWrap: 'wrap' }}>
                    <Typography variant="caption" sx={{ fontWeight: 'bold', color: '#fff' }}>{diff.factor}</Typography>
                    {diff.winner && <Typography variant="caption" sx={{ color: COLOR_GOOD, fontWeight: 'bold' }}>→ {diff.winner}</Typography>}
                    {diff.score_difference && <Chip label={diff.score_difference} size="small" sx={{ height: 16, fontSize: '0.6rem', bgcolor: 'rgba(255,255,255,0.06)', color: '#9e9e9e' }} />}
                  </Box>
                  <Typography variant="caption" sx={{ color: '#b0b0b0', lineHeight: 1.4, display: 'block' }}>{diff.explanation}</Typography>
                </Box>
              </Box>
            ))}
          </CardContent>
        </Card>
      )}

      {/* Final Recommendation */}
      {aiAnalysis.final_recommendation && (
        <Card elevation={1} sx={{ ...CARD_SX, background: `linear-gradient(135deg, ${BRAND_PURPLE}12 0%, ${BRAND_PURPLE}05 100%)` }}>
          <CardContent sx={{ p: 2 }}>
            <Typography variant="subtitle2" sx={{ fontWeight: 'bold', color: BRAND_PURPLE, mb: 1 }}>
              Final Recommendation
            </Typography>
            <Typography variant="body2" sx={{ lineHeight: 1.8, color: '#e0e0e0' }}>
              {aiAnalysis.final_recommendation}
            </Typography>
          </CardContent>
        </Card>
      )}

      {/* Executive Summary — opens full modal */}
      {aiAnalysis && (() => {
        const recommendedConfig = comparisonData.configurations?.find(
          c => c.configuration_name === aiAnalysis.recommended_lineup
        );
        return (
          <>
            <Card elevation={1} sx={CARD_SX}>
              <CardContent sx={{ p: 2, display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
                <Typography variant="subtitle2" sx={{ fontWeight: 'bold', color: BRAND_PURPLE }}>
                  Executive Summary
                </Typography>
                <Button
                  variant="contained"
                  size="small"
                  startIcon={<SummaryIcon />}
                  onClick={() => setSummaryModalOpen(true)}
                  disabled={!recommendedConfig}
                  sx={{ bgcolor: BRAND_PURPLE, '&:hover': { bgcolor: BRAND_PURPLE } }}
                >
                  View Summary
                </Button>
              </CardContent>
            </Card>

            {recommendedConfig && (
              <ExecutiveSummaryModal
                open={summaryModalOpen}
                onClose={() => setSummaryModalOpen(false)}
                configurationId={recommendedConfig.configuration_id}
                configurationName={recommendedConfig.configuration_name}
                projectName={comparisonData.project_name}
                aiAnalysis={aiAnalysis}
                savedSummary={recommendedConfig.executive_summary || null}
                comparisonData={comparisonData}
                opportunityMeta={{
                  ...opportunityMeta,
                  teamMembers: recommendedConfig.team_members || [],
                }}
              />
            )}
          </>
        );
      })()}
    </Box>
  );
};

AIDecisionAdvisor.propTypes = {
  comparisonData: PropTypes.object.isRequired,
  selectedConfigIds: PropTypes.array.isRequired,
  onClose: PropTypes.func,
  opportunityMeta: PropTypes.object,
};

export default AIDecisionAdvisor;
